// =====================================================================
// photos.js — ré-encodage d'une photo dans le navigateur, AVANT l'envoi
// =====================================================================
// Commande prête Nespart (13-10-2026). Reprise au départ de la logique de
// pages/Embauche.jsx (M-011, carte d'identité) — Embauche.jsx garde sa propre
// copie, NON modifiée : les deux ont divergé volontairement le 14/10 (ci-dessous).
//
// Une photo prise au téléphone ou au PDA pèse 2 à 6 Mo ; sur le wifi du dépôt,
// l'envoi dure et 8 photos par unité pèseraient sur le stockage et sur la page
// client. On réduit donc l'image ICI : côté long 2000 px (l'étiquette reste
// lisible), JPEG 85 % — ~0,5 Mo mesuré.
//
// VIE PRIVÉE (14/10) : ces photos partent chez le client (Nespart), servies
// telles quelles. TOUTE photo est donc redessinée sur un canvas puis ré-encodée
// en JPEG — quels que soient sa taille et son format — ce qui retire les
// métadonnées (EXIF : position GPS, appareil, date…). Jamais l'original. Une
// photo que le navigateur ne sait pas décoder est REFUSÉE (message clair) : elle
// ne part pas avec ses métadonnées.
// =====================================================================

export const PHOTO_COTE_MAX = 2000;
export const PHOTO_QUALITE = 0.85;
export const MSG_PHOTO_ILLISIBLE = 'Photo illisible sur cet appareil : reprenez-la avec l\'appareil photo (JPEG ou PNG)';

const DELAI_MS = 10000;

// Taille de l'image ré-encodée : côté long ramené à 2000 px, jamais agrandie.
export function dimensionsCible(largeur, hauteur) {
  const echelle = Math.min(1, PHOTO_COTE_MAX / Math.max(largeur, hauteur));
  return { w: Math.max(1, Math.round(largeur * echelle)), h: Math.max(1, Math.round(hauteur * echelle)) };
}
// « IMG_1234.HEIC » → « IMG_1234.jpg » (le fichier envoyé est toujours un JPEG).
export const nomJpeg = (nom) => `${String(nom || 'photo').replace(/\.[^.]+$/, '') || 'photo'}.jpg`;

// Décodage de la photo, borné dans le temps : un décodage qui ne répond jamais
// (téléphone à court de mémoire) est abandonné ET nettoyé (image + URL libérées),
// sinon un nouvel essai empilerait un 2e décodage sur le 1er.
function chargerImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const fin = () => { clearTimeout(minuterie); img.onload = null; img.onerror = null; URL.revokeObjectURL(url); };
    const minuterie = setTimeout(() => { fin(); img.src = ''; reject(new Error('délai dépassé')); }, DELAI_MS);
    img.onload = () => { fin(); resolve(img); };
    img.onerror = () => { fin(); reject(new Error('image illisible')); };
    img.src = url;
  });
}

function versBlob(canvas) {
  return new Promise((resolve, reject) => {
    const minuterie = setTimeout(() => reject(new Error('délai dépassé')), DELAI_MS);
    canvas.toBlob((blob) => { clearTimeout(minuterie); resolve(blob); }, 'image/jpeg', PHOTO_QUALITE);
  });
}

// Un ré-encodage par fichier choisi : un nouvel essai après une erreur réseau ne refait pas
// le travail (lent sur un téléphone d'entrée de gamme). Un ÉCHEC n'est pas gardé (re-tentable).
const photosReduites = new WeakMap();

/**
 * Photo prête à partir : TOUJOURS un JPEG ré-encodé (sans métadonnées), côté long ≤ 2000 px.
 * Rejette avec { code: 'PHOTO_ILLISIBLE', message } si le navigateur ne peut pas la décoder :
 * l'original n'est jamais envoyé.
 */
export function reduirePhoto(file) {
  if (!file) return Promise.reject(Object.assign(new Error(MSG_PHOTO_ILLISIBLE), { code: 'PHOTO_ILLISIBLE' }));
  if (!photosReduites.has(file)) {
    const p = reencoder(file);
    photosReduites.set(file, p);
    p.catch(() => photosReduites.delete(file));
  }
  return photosReduites.get(file);
}

async function reencoder(file) {
  let canvas = null;
  try {
    // <img> + drawImage respecte l'orientation EXIF (photo prise en portrait) avant que le
    // ré-encodage ne retire l'EXIF ; createImageBitmap ne le fait pas pareil partout.
    const img = await chargerImage(file);
    // Image « décodée » sans taille (SVG sans dimensions…) : jamais un JPEG blanc de 1 × 1 qui
    // passerait pour la photo obligatoire.
    if (!(img.naturalWidth > 0 && img.naturalHeight > 0)) throw new Error('image sans dimensions');
    const { w, h } = dimensionsCible(img.naturalWidth, img.naturalHeight);
    canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; // PNG transparent → fond blanc une fois en JPEG
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await versBlob(canvas);
    if (!blob || !blob.size) throw new Error('encodage JPEG impossible');
    return new File([blob], nomJpeg(file.name), { type: 'image/jpeg', lastModified: Date.now() });
  } catch (e) {
    throw Object.assign(new Error(MSG_PHOTO_ILLISIBLE), { code: 'PHOTO_ILLISIBLE', cause: e });
  } finally {
    // iOS Safari plafonne la mémoire totale des canvas : on libère tout de suite.
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}
